import { linkOldRefs } from "@/lib/work-item-refs";
import { marked } from "marked";
import { useEffect, useState } from "react";

marked.setOptions({ breaks: true, gfm: true });

/**
 * Markdown renderer. Port of the Next-side `markdown.tsx` minus
 * the `process.env.OPENCLOUD_URL` fallback — `process` isn't
 * defined in the browser bundle, so the client passes
 * `openCloudBase` explicitly (or omits it, getting plain links).
 *
 * With `lightboxImages`, embedded images render as capped thumbnails
 * and a click opens them full-size in a lightbox overlay — used for
 * work-item descriptions and comments.
 */

type Props = {
  content: string;
  className?: string;
  /** Absolute prefix for resolving root-relative `src`/`href` (GitLab uploads). */
  relativeUrlBase?: string | undefined;
  /** OpenCloud base — links under it render as a chip. No env fallback here. */
  openCloudBase?: string | undefined;
  /** Hub project id — when set, `/uploads/<sha>/<file>` paths (the
   *  format GitLab returns for project attachments) get rewritten to
   *  Hub's `/api/gitlab-uploads/<id>/uploads/<sha>/<file>` proxy so
   *  images render without a GitLab session. */
  gitlabUploadProjectId?: string | undefined;
  /** Render images as click-to-zoom thumbnails with a lightbox. */
  lightboxImages?: boolean;
  /** Old per-project `#n` → global id, shown and linked as `#<id>`. */
  workItemRefs?: Record<string, number> | undefined;
};

export function Markdown({
  content,
  className,
  relativeUrlBase,
  openCloudBase,
  gitlabUploadProjectId,
  lightboxImages,
  workItemRefs,
}: Props) {
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  if (!content) return null;
  let html = marked.parse(workItemRefs ? linkOldRefs(content, workItemRefs) : content) as string;
  html = applyGitlabImageSizes(html);
  if (relativeUrlBase) html = rewriteRelativeUrls(html, relativeUrlBase);
  if (openCloudBase) html = enhanceOpenCloudLinks(html, openCloudBase);
  // GitLab project uploads embed as `/uploads/<sha>/<file>` paths —
  // resolve them through Hub's authenticated proxy so the bytes load
  // for any signed-in viewer (no GitLab cookie required).
  if (gitlabUploadProjectId) {
    html = rewriteGitlabUploadUrls(html, gitlabUploadProjectId);
  }
  // Must run last: a Hub-uploaded image embedded in a GitLab-owned
  // field (a work-item description, a comment) comes back with
  // GitLab's host glued in front of Hub's `/api/uploads/` path —
  // GitLab absolutises every root-relative link it stores. Strip any
  // host so the path resolves against Hub, which actually serves it.
  html = normalizeHubUploadUrls(html);

  // Image styling: every image is capped at the column width. In
  // lightbox mode they also get a thumbnail height cap + zoom cursor;
  // the click handler below opens the full-size overlay.
  const imgClasses = lightboxImages
    ? "[&_img]:max-h-60 [&_img]:w-auto [&_img]:cursor-zoom-in [&_img]:rounded-md [&_img]:border"
    : "[&_img]:h-auto";

  function onClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!lightboxImages) return;
    const target = e.target as HTMLElement;
    if (target instanceof HTMLImageElement) {
      // Don't follow a surrounding link or trip a parent click-to-edit
      // handler — open the lightbox instead.
      e.preventDefault();
      e.stopPropagation();
      setLightbox({ src: target.currentSrc || target.src, alt: target.alt });
    }
  }

  return (
    <>
      {/* `overflow-x-auto` + `break-words` keep wide tables, fenced
          code and long unbroken URLs inside the column. The onClick is
          delegation that zooms an embedded image — not a control, so
          no keyboard handler applies to the container itself. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click delegates image-zoom; the container is not a control */}
      <div
        className={`prose prose-sm max-w-none overflow-x-auto break-words dark:prose-invert [&_img]:max-w-full ${imgClasses} ${className ?? ""}`}
        onClick={onClick}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: internal tool, content is from trusted users
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </>
  );
}

/**
 * Full-screen image overlay. Click anywhere or press Escape to close.
 */
function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape-to-close is wired via a document keydown listener
    // biome-ignore lint/a11y/useSemanticElements: lightweight transient image overlay; div + role is sufficient
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Image preview"}
      data-testid="image-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6 cursor-zoom-out"
    >
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full rounded-md shadow-2xl"
        // The image itself is part of the click-to-close surface.
      />
    </div>
  );
}

/**
 * Apply GitLab's image-size extension. GitLab serialises sized
 * images as `![alt](url){width=900 height=558}`; `marked` emits
 * the curly suffix as literal text. Lift width/height onto the
 * `<img>` and drop the suffix.
 */
export function applyGitlabImageSizes(html: string): string {
  return html.replace(
    /(<img\b[^>]*?)(\s*\/?>)\s*\{([^}]+)\}/g,
    (_full, imgOpen: string, close: string, attrs: string) => {
      const width = attrs.match(/\bwidth\s*=\s*"?(\d+)"?/i)?.[1];
      const height = attrs.match(/\bheight\s*=\s*"?(\d+)"?/i)?.[1];
      let out = imgOpen;
      if (width) out += ` width="${width}"`;
      if (height) out += ` height="${height}"`;
      out += close;
      return out;
    },
  );
}

/**
 * Strip any host from a Hub `/api/uploads/<file>` URL so it resolves
 * against Hub's own origin. GitLab rewrites every root-relative link
 * in a description/comment it stores to an absolute one on its own
 * host — which 404s, since Hub (not GitLab) serves `/api/uploads`.
 * The stored path stays unambiguously Hub's; only the host is wrong.
 */
export function normalizeHubUploadUrls(html: string): string {
  return html.replace(
    /\b(src|href)="https?:\/\/[^"/]+(\/api\/uploads\/[^"]*)"/g,
    (_m, attr: string, path: string) => `${attr}="${path}"`,
  );
}

/**
 * Rewrite project-relative `/uploads/<sha>/<filename>` paths — what
 * GitLab returns when you upload a file via its issue-attachment
 * endpoint — to Hub's authenticated proxy
 * `/api/gitlab-uploads/<hubProjectId>/uploads/<sha>/<filename>`.
 *
 * Without the proxy, a browser sees the bare `/uploads/...` path,
 * resolves it against Hub's host (where there's no such route), and
 * the image breaks. The proxy fetches the bytes from GitLab using
 * Hub's service token, so any signed-in viewer sees the image even
 * without a separate GitLab session.
 *
 * Hub's own `/api/uploads/` is unambiguously distinct, so the match
 * never confuses the two.
 */
export function rewriteGitlabUploadUrls(html: string, hubProjectId: string): string {
  return html.replace(
    /\b(src|href)="\/uploads\/([a-f0-9]+\/[^"]+)"/gi,
    (_m, attr: string, rest: string) =>
      `${attr}="/api/gitlab-uploads/${hubProjectId}/uploads/${rest}"`,
  );
}

/** Prefix root-relative `src`/`href` with `base`. */
export function rewriteRelativeUrls(html: string, base: string): string {
  const trimmed = base.replace(/\/$/, "");
  return html.replace(
    /\b(src|href)="\/(?!\/)([^"]*)"/g,
    (_m, attr, rest) => `${attr}="${trimmed}/${rest}"`,
  );
}

/** Wrap `<a>` links under `base` in a styled OpenCloud chip. */
export function enhanceOpenCloudLinks(html: string, base: string): string {
  if (!base) return html;
  const trimmed = base.replace(/\/$/, "");
  const pattern = new RegExp(`<a\\s+href="(${escapeRegExp(trimmed)}[^"]*)">([^<]*)<\\/a>`, "g");
  const icon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>';
  return html.replace(pattern, (_full, href: string, text: string) => {
    let display = text;
    if (text === href) {
      const pathOnly = (href.split("?")[0] ?? href).split("#")[0] ?? href;
      const lastSegment = pathOnly.split("/").filter(Boolean).pop() ?? href;
      try {
        display = decodeURIComponent(lastSegment);
      } catch {
        display = lastSegment;
      }
    }
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 no-underline hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">${icon}<span>${escapeHtml(display)}</span></a>`;
  });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
